import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { makeMatcher } from '../../services/search';
import SearchField from '../SearchField';
import AppFormModal from '../AppFormModal';

export default function AdminApps() {
  const [apps, setApps] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [accessApp, setAccessApp] = useState(null);
  const [search, setSearch] = useState('');

  const load = () => {
    Promise.all([api.get('/applications/admin/all'), api.get('/groups'), api.get('/users')])
      .then(([a, g, u]) => { setApps(a.data.data || []); setGroups(g.data.data || []); setUsers(u.data.data || []); })
      .catch(e => setMsg(`Erreur : ${e.response?.data?.message || e.message}`))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Le formulaire vit dans AppFormModal, partagé avec le portail : un admin
  // peut y modifier un outil sans passer par l'administration.
  const openCreate = () => { setEditApp(null); setShowModal(true); };
  const openEdit = (app) => { setEditApp(app); setShowModal(true); };

  // Filtrage local : la liste tient en mémoire, inutile de repasser par l'API.
  const correspond = makeMatcher(search);
  const appsFiltrees = apps.filter(a =>
    correspond(a.name, a.description, a.url, ...(a.groups || []).map(g => g.name)));

  const hasAccess = (app, userId) => (app.users || []).some(u => u.id === userId);

  const toggleAccess = async (app, userId) => {
    if (hasAccess(app, userId)) await api.delete(`/applications/admin/${app.id}/revoke`, { data: { userId } });
    else await api.post(`/applications/admin/${app.id}/grant`, { userId });
    const { data } = await api.get('/applications/admin/all');
    const apps2 = data.data || [];
    setApps(apps2);
    setAccessApp(apps2.find(a => a.id === app.id));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette application ?')) return;
    await api.delete(`/applications/admin/${id}`);
    load();
  };

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>Applications</h2>
        <SearchField
          className="admin-search"
          value={search}
          onChange={setSearch}
          placeholder="Rechercher une application..."
        />
        <button onClick={openCreate} style={styles.addBtn}>+ Nouvelle application</button>
      </div>
      {msg && <div style={{ ...styles.msg, background: msg.startsWith('Erreur') ? '#fff0ed' : '#e8f8e8', color: msg.startsWith('Erreur') ? '#E46138' : '#2d6a2d' }}>{msg}</div>}

      {loading ? <p>Chargement...</p> : (
        <div style={styles.table}>
          <div style={styles.tableHeader}>
            <span>Application</span><span>Groupes</span><span>URL</span><span>Statut</span><span>Actions</span>
          </div>
          {appsFiltrees.length === 0 ? (
            <div style={styles.emptyRow}>
              {search.trim()
                ? `Aucune application ne correspond à « ${search.trim()} ».`
                : 'Aucune application enregistrée pour le moment.'}
            </div>
          ) : appsFiltrees.map(app => (
            <div key={app.id} style={styles.tableRow}>
              <div style={styles.appName}>
                <div style={{ ...styles.appIcon, background: app.color || '#0F56E3' }}>
                  {app.iconType === 'emoji' ? app.icon : (app.icon ? '🔗' : app.name[0])}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{app.name}</div>
                  <div style={{ fontSize: 12, color: '#7F7F7F' }}>{app.description?.slice(0, 40)}</div>
                </div>
              </div>
              <div style={styles.badgeList}>
                {(app.groups || []).length === 0
                  ? <span style={{ fontSize: 12, color: '#7F7F7F' }}>—</span>
                  : app.groups.map(g => <span key={g.id} style={styles.badge}>{g.name}</span>)}
              </div>
              <a href={app.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#0F56E3', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{app.url}</a>
              <span style={{ ...styles.badge, background: app.isActive ? '#e8f8e8' : '#fafafa', color: app.isActive ? '#2d6a2d' : '#7F7F7F' }}>
                {app.isActive ? '✓ Actif' : '✗ Inactif'}
              </span>
              <div style={styles.actions}>
                <button onClick={() => setAccessApp(app)} style={styles.accessBtn} title="Gérer les accès">👥</button>
                <button onClick={() => openEdit(app)} style={styles.editBtn}>✏</button>
                <button onClick={() => handleDelete(app.id)} style={styles.deleteBtn}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <AppFormModal
          app={editApp}
          groups={groups}
          onClose={() => setShowModal(false)}
          onSaved={() => { setMsg('Sauvegardé avec succès !'); load(); setTimeout(() => setMsg(''), 3000); }}
        />
      )}

      {accessApp && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3>Accès — {accessApp.name}</h3>
              <button onClick={() => setAccessApp(null)} style={styles.closeBtn}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <p style={{ fontSize: 13, color: '#7F7F7F', marginBottom: 8 }}>
                Les administrateurs voient toujours toutes les applications. Cochez les utilisateurs qui doivent avoir accès à celle-ci.
              </p>
              {users.filter(u => u.role !== 'admin').length === 0 ? (
                <p style={{ fontSize: 13, color: '#7F7F7F' }}>Aucun utilisateur non-admin.</p>
              ) : users.filter(u => u.role !== 'admin').map(u => (
                <label key={u.id} style={styles.accessRow}>
                  <input
                    type="checkbox"
                    checked={hasAccess(accessApp, u.id)}
                    onChange={() => toggleAccess(accessApp, u.id)}
                  />
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{u.firstName} {u.lastName}</span>
                  <span style={{ fontSize: 12, color: '#7F7F7F' }}>{u.email}</span>
                </label>
              ))}
            </div>
            <div style={styles.modalFooter}>
              <button onClick={() => setAccessApp(null)} style={styles.saveBtn}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: '#062259' },
  addBtn: { marginLeft: 'auto', flexShrink: 0, background: '#062259', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  emptyRow: { padding: '28px 20px', textAlign: 'center', fontSize: 14, color: '#5f6673' },
  msg: { padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  table: { background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  tableHeader: { display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 1fr 116px', gap: 16, padding: '12px 20px', background: '#f4f6f9', fontSize: 12, fontWeight: 600, color: '#7F7F7F', textTransform: 'uppercase' },
  tableRow: { display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 1fr 116px', gap: 16, padding: '16px 20px', alignItems: 'center', borderBottom: '1px solid #f4f6f9' },
  appName: { display: 'flex', alignItems: 'center', gap: 12 },
  appIcon: { width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 700, flexShrink: 0 },
  badge: { display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontSize: 12, background: '#EEF3FF', color: '#0F56E3' },
  badgeList: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  actions: { display: 'flex', gap: 6 },
  editBtn: { background: '#EEF3FF', color: '#0F56E3', border: 'none', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 14 },
  deleteBtn: { background: '#fff0ed', color: '#E46138', border: 'none', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 14 },
  accessBtn: { background: '#f4f6f9', color: '#062259', border: 'none', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 14 },
  accessRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f4f6f9', cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #f4f6f9' },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#7F7F7F' },
  modalBody: { padding: '24px', display: 'flex', flexDirection: 'column', gap: 16 },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #f4f6f9' },
  saveBtn: { background: '#062259', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
